/**
 * RatedScore — the handicap score used for leaderboards (docs/GAME_DESIGN.md
 * § 5, revised 2026-09-24; rationale, worked examples and anti-exploit notes:
 * docs/SCORING.md).
 *
 * A run's RatedScore is an EFFICIENCY: credited work per unit of time, with every
 * problem's par time as its unit of work (par already encodes how hard that
 * problem is), weighted by relevance and with a prior:
 *
 *   Run Rated = max(0, round(UNIT × Σᵢ mᵢ·w(Dᵢ)·cᵢ / (Σᵢ mᵢ·t̂ᵢ + N0)))
 *
 * - cᵢ    credit: correct → 1, wrong/timeout → −λ
 * - t̂ᵢ    time spent in par units: clamp(response / par, TAU_FLOOR, TAU_CAP)
 *         (the floor stops tiny times on trivial problems from exploding the ratio;
 *         the cap stops one long timeout from swamping a run)
 * - w(D)  difficulty weight D^η: harder bands earn a little more
 * - mᵢ    relevance weight: answers far below the anchor level count less, in both directions
 * - N0    prior: N0 par-units of time with no credit, so short runs shrink toward 0
 *
 * An on-level learner at par speed with 80% accuracy scores ≈ 1000 × (0.8 − 0.2λ)
 * ≈ 680; answering the same problems in 0.7 × par lifts that to ≈ 950.
 *
 * The anchor is max(referenceRating, peak rating of the run). The final score
 * uses the run's final peak; during play the engine shows the same formula with
 * the peak so far (see game/summary.ts runRatedScore).
 */
import { SCORING } from '../config.ts';
import { clampRating, levelForRating } from '../adaptive/rating.ts';
import { clamp } from '../math/stats.ts';
import type { AnswerRecord } from '../types.ts';
import type { ScoringConstants } from './types.ts';

/** The answer fields RatedScore reads. */
export type RatedAnswer = Pick<AnswerRecord, 'outcome' | 'responseMs' | 'parTimeMs' | 'level' | 'difficultyIndex'>;

/** w(D) = D^η: harder bands earn a little more, not proportionally more (1.00 at level 1 … 1.31 at level 24). */
export function difficultyWeight(D: number, k: ScoringConstants = SCORING): number {
  return Math.max(D, 0) ** k.RATED_D_ETA;
}

/**
 * Relevance anchor: the higher of the server's reference rating (the player's
 * established peak, decaying slowly) and the run's own peak rating.
 */
export function ratingAnchor(referenceRating: number, peakRating: number): number {
  return Math.max(clampRating(referenceRating), clampRating(peakRating));
}

/**
 * Relevance weight m = clamp(1 − MU·max(0, gap − GAP_FREE), M_FLOOR, 1), with
 * gap = level(anchorRating) − problemLevel. Problems within GAP_FREE levels of
 * the anchor level (or above it) count in full; far easier ones count less —
 * for right AND wrong answers, so neither farming nor tanking easy problems
 * moves the score much.
 */
export function relevanceWeight(problemLevel: number, anchorRating: number, k: ScoringConstants = SCORING): number {
  return relevanceForGap(levelForRating(anchorRating) - problemLevel, k);
}

function relevanceForGap(gap: number, k: ScoringConstants): number {
  return clamp(1 - k.RATED_MU * Math.max(0, gap - k.RATED_GAP_FREE), k.RATED_M_FLOOR, 1);
}

/** Credit c: correct → 1, wrong/timeout → −λ. */
export function answerCredit(record: Pick<AnswerRecord, 'outcome'>, k: ScoringConstants = SCORING): number {
  return record.outcome === 'correct' ? 1 : -k.RATED_WRONG_LAMBDA;
}

/** Time spent in par units, t̂ = clamp(response / par, TAU_FLOOR, TAU_CAP). */
export function answerTime(record: Pick<AnswerRecord, 'responseMs' | 'parTimeMs'>, k: ScoringConstants = SCORING): number {
  const tau = record.parTimeMs > 0 ? record.responseMs / record.parTimeMs : 1;
  return clamp(tau, k.RATED_TAU_FLOOR, k.RATED_TAU_CAP);
}

/**
 * One answer's weighted credit UNIT·m·w(D)·c, in rated points: its term in the
 * numerator of the run efficiency. The run score divides the sum of these by
 * Σ m·t̂ + N0, so this is NOT an amount added to the run score.
 */
export function weightedAnswerValue(record: RatedAnswer, anchorRating: number, k: ScoringConstants = SCORING): number {
  return k.RATED_UNIT * relevanceWeight(record.level, anchorRating, k) * difficultyWeight(record.difficultyIndex, k) * answerCredit(record, k);
}

/**
 * Run RatedScore = max(0, round(UNIT × Σ m·w·c / (Σ m·t̂ + N0))) over every
 * answer of the run (timeouts included), with m anchored at `anchorRating`.
 * Pure and O(answers); an empty run scores 0.
 */
export function ratedScoreForRun(history: readonly RatedAnswer[], anchorRating: number, k: ScoringConstants = SCORING): number {
  const anchorLevel = levelForRating(anchorRating);
  let credit = 0;
  let time = 0;
  for (const record of history) {
    const m = relevanceForGap(anchorLevel - record.level, k);
    credit += m * difficultyWeight(record.difficultyIndex, k) * answerCredit(record, k);
    time += m * answerTime(record, k);
  }
  const denominator = time + k.RATED_PRIOR_N0;
  if (!(denominator > 0)) return 0;
  // Math.max ranks +0 above −0, so a score that rounds to −0 comes out as 0.
  return Math.max(0, Math.round((k.RATED_UNIT * credit) / denominator));
}
